import {Button, Input} from "antd";
import {useEffect, useState} from "react";
import {CloseOutlined, LeftOutlined, RightOutlined} from "@ant-design/icons";
import useUserContext from "../../../hooks/useUserContext";
import "./step1.css";
import {FormValuesProps} from "../../modals/notificationModals/NewNotificationModal";
import Step1Fields from "./Step1Fields";
import MessagePreview from "../../messagePreview/MessagePreview";
import {useSteps} from "../../../hooks/useSteps";
import {LANGUAGES} from "../../../utils/languages";

interface StepProps {
    formValues: FormValuesProps;
    setFormValues: (values: FormValuesProps) => void;
    flowTriggerId: number;
    isFirstStep?: boolean;
}

const Step1 = ({ formValues, setFormValues, flowTriggerId, isFirstStep = true }: StepProps) => {
    const { user, selectedDomain } = useUserContext();
    const { goBack, goNext } = useSteps();
    const [feedback, setFeedback] = useState('');
    const { title, language, content, isCoupon, isWithImage, imageUrl } = formValues;
    const setTitle = (newValue: string) => setFormValues({ ...formValues, title: newValue });
    const setLanguage = (newValue: string) => setFormValues({ ...formValues, language: newValue });
    const setContent = (newValue: string) => setFormValues({ ...formValues, content: newValue });
    const setIsWithImage = (newValue: boolean) => setFormValues({ ...formValues, isWithImage: newValue });
    const setImageUrl = (newValue: string) => setFormValues({ ...formValues, imageUrl: newValue });
    const setIsCoupon = (newValue: boolean) => setFormValues({ ...formValues, isCoupon: newValue });
    const setButton1Text = (newValue: string) => setFormValues({ ...formValues, button1Text: newValue });
    const setButton2Text = (newValue: string) => setFormValues({ ...formValues, button2Text: newValue });

    const selectedLanguage = LANGUAGES.find(lang => lang.key === language);
    const direction = selectedLanguage?.direction === 'rtl' ? 'rtl' : 'ltr';

    useEffect(() => {
        if(title.length < 5) {
            setFeedback("Tip: Let's add a title that make sense so you'll be able to recognize it in the system")
        } else if(!content) {
            setFeedback("Ok, time to add some content. Make it as appealing as possible for the client, and use the Dynamic Placeholders to add more informative details, like the cart, the price and the client's name. You can see a preview of your message right above this tip.");
        } else if(!isWithImage) {
            setFeedback("Tip: Adding a product image to the message will increase the conversion rate. This is highly recommended. Just click on the 'Include Product Image' box")
        } else if(content && !content.includes('{{name}}') || !content.includes('{{cart}}') || !content.includes('{{price}}')) {
            setFeedback("Tip: Don't forget to add Dynamic Placeholders to make the message more informative for your client");
        } else {
            setFeedback("Looks amazing! I think we're good to go to the next step")
        }
    }, [title, content, isWithImage])

    return <>
        <div className="step-container step-1">
            <div className="left">
                <div className="top">
                    <Step1Fields
                        title={title} setTitle={setTitle}
                        language={language} setLanguage={setLanguage}
                        content={content} setContent={setContent}
                        isWithImage={isWithImage} setIsWithImage={setIsWithImage}
                        imageUrl={imageUrl} setImageUrl={setImageUrl}
                        isCoupon={isCoupon} setIsCoupon={setIsCoupon}
                        flowTriggerId={flowTriggerId}
                        requiresConsent={formValues.requiresConsent}
                        direction={direction}
                    />
                </div>
                <div className="bottom">
                    <div className="buttons">
                        <Button type="default" size="large" className="back-step-button" onClick={() => goBack()} icon={isFirstStep ? <CloseOutlined /> : <LeftOutlined />}>{isFirstStep ? 'Cancel' : 'Back'}</Button>
                        <Button type="primary" size="large" className="next-step-button" onClick={() => goNext()} icon={<RightOutlined />} disabled={!title || !content}>Next Step</Button>
                    </div>
                </div>
            </div>
            <div className="right">
                <MessagePreview
                    text={content}
                    isWithImage={isWithImage}
                    feedback={feedback}
                    direction={direction}
                    flowTriggerId={flowTriggerId}
                    language={language}
                    button1Text={formValues.button1Text}
                    button2Text={formValues.button2Text}
                    setButton1Text={setButton1Text}
                    setButton2Text={setButton2Text}
                />
            </div>

        </div>
    </>
}

export default Step1;